import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'Invalid Date';
  // Use toLocaleString (not toLocaleDateString) to properly include time
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Format a calendar date (birthdate / DOB) — timezone-independent, date-only.
 *
 * A DATE column has no time/zone. Do NOT use `formatDate` (above) for it: that
 * does `new Date(str).toLocaleString()`, which interprets a "YYYY-MM-DD" (or
 * UTC-midnight ISO) value as a UTC instant and renders the *previous* day for
 * viewers behind UTC. Read the Y-M-D parts and build a local-midnight Date.
 */
export function formatBirthdate(date: string | null | undefined): string {
  if (!date) return 'N/A';
  const m = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return 'Invalid Date';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Age in whole years from a birthdate — timezone-independent (compares calendar
 * parts only; never shifts at the day boundary regardless of viewer TZ).
 */
export function calculateAge(birthdate: string | null | undefined): number | null {
  if (!birthdate) return null;
  const m = String(birthdate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const now = new Date();
  const nowMonth = now.getMonth() + 1;
  let age = now.getFullYear() - year;
  if (nowMonth < month || (nowMonth === month && now.getDate() < day)) age--;
  return age;
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

/**
 * Display a value or a placeholder for empty values
 * Use this for consistent empty state display across the admin panel
 */
export function displayValue(value: string | number | null | undefined, placeholder = '—'): string {
  if (value === null || value === undefined || value === '') {
    return placeholder;
  }
  return String(value);
}

/**
 * Resolve a human-readable name for a user, fail-safe against null/missing fields.
 *
 * Precedence: display_name → "first last" (omitting empty parts) → email local-part → fallback.
 * Guards against the `${first_name} ${last_name}` interpolation rendering the literal
 * string "undefined undefined" when name fields are null (the reviewer@ admin-seed account
 * has no name parts). Mirrors the backend resolveFirstName email-local-part fallback.
 */
export function resolveUserDisplayName(
  user: {
    display_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  },
  fallback = 'Unknown user'
): string {
  const display = user.display_name?.trim();
  if (display) return display;

  const fullName = [user.first_name, user.last_name]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' ');
  if (fullName) return fullName;

  const localPart = user.email?.split('@')[0]?.trim();
  if (localPart) return localPart;

  return fallback;
}

/**
 * Format a date for display, with special handling for "never" cases
 */
export function formatDateOrNever(
  date: Date | string | null | undefined,
  options: { neverText?: string; naText?: string } = {}
): string {
  const { neverText = 'Never', naText = '—' } = options;

  // If explicitly null, the user has never performed this action
  if (date === null) {
    return neverText;
  }

  // If undefined, we don't have data
  if (date === undefined) {
    return naText;
  }

  // Otherwise format the date
  return formatDate(date);
}
